import React, { useState, useEffect } from 'react';
import { Plus, Search, Package, AlertTriangle, TrendingDown, TrendingUp, Edit, Trash2, History, Filter, MoreVertical, CheckSquare, Square, Building2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { PermissionGate } from '../../context/PermissionContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { id: 'fabric', name: 'Fabrics', icon: '🧵' },
  { id: 'hardware', name: 'Hardware', icon: '🔩' },
  { id: 'motor', name: 'Motors', icon: '⚡' },
  { id: 'accessory', name: 'Accessories', icon: '📦' },
];

const UNITS = ['each', 'yard', 'roll', 'box', 'set', 'pair'];

const MOVEMENT_TYPES = [
  { id: 'receive', name: 'Receive Stock', color: 'text-green-600' },
  { id: 'use', name: 'Use/Consume', color: 'text-red-600' },
  { id: 'adjust', name: 'Adjustment', color: 'text-amber-600' },
  { id: 'return', name: 'Return', color: 'text-blue-600' },
];

const emptyItem = {
  name: '',
  sku: '',
  category: 'fabric',
  subcategory: '',
  description: '',
  quantity: 0,
  unit: 'each',
  min_stock_level: 0,
  unit_cost: 0,
  retail_price: '',
  primary_supplier_id: '',
  supplier_sku: '',
  lead_time_days: '',
  color: '',
  material: '',
  width: '',
};

export const InventoryTab = ({ token }) => {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyItem);
  
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [movementItem, setMovementItem] = useState(null);
  const [movementData, setMovementData] = useState({ quantity: 0, movement_type: 'receive', notes: '' });
  
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);
  const [movements, setMovements] = useState([]);
  
  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkSupplierId, setBulkSupplierId] = useState('');
  const [assigningBulk, setAssigningBulk] = useState(false);

  useEffect(() => {
    fetchItems();
    fetchSuppliers();
    fetchStats();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category', categoryFilter);
      if (showLowStock) params.append('low_stock', 'true');
      
      const response = await axios.get(`${API_URL}/admin/inventory?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/inventory/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const saveItem = async () => {
    if (!formData.name.trim()) {
      toast.error('Item name is required');
      return;
    }

    try {
      if (editingItem) {
        await axios.put(`${API_URL}/admin/inventory/${editingItem.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Item updated');
      } else {
        await axios.post(`${API_URL}/admin/inventory`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Item created');
      }
      setShowItemModal(false);
      setEditingItem(null);
      setFormData(emptyItem);
      fetchItems();
      fetchStats();
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${API_URL}/admin/inventory/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item deleted');
      fetchItems();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const recordMovement = async () => {
    if (!movementData.quantity || movementData.quantity === 0) {
      toast.error('Quantity is required');
      return;
    }

    try {
      const qty = movementData.movement_type === 'use' 
        ? -Math.abs(movementData.quantity)
        : Math.abs(movementData.quantity);
      
      await axios.post(`${API_URL}/admin/inventory/${movementItem.id}/movement`, {
        ...movementData,
        quantity: qty
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Movement recorded');
      setShowMovementModal(false);
      setMovementItem(null);
      setMovementData({ quantity: 0, movement_type: 'receive', notes: '' });
      fetchItems();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record movement');
    }
  };

  const viewHistory = async (item) => {
    setHistoryItem(item);
    try {
      const response = await axios.get(`${API_URL}/admin/inventory/${item.id}/movements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMovements(response.data);
      setShowHistoryModal(true);
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      sku: item.sku || '',
      category: item.category || 'fabric',
      subcategory: item.subcategory || '',
      description: item.description || '',
      quantity: item.quantity || 0,
      unit: item.unit || 'each',
      min_stock_level: item.min_stock_level || 0,
      unit_cost: item.unit_cost || 0,
      retail_price: item.retail_price || '',
      primary_supplier_id: item.primary_supplier_id || '',
      supplier_sku: item.supplier_sku || '',
      lead_time_days: item.lead_time_days || '',
      color: item.color || '',
      material: item.material || '',
      width: item.width || '',
    });
    setShowItemModal(true);
  };

  // Bulk selection functions
  const toggleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map(item => item.id)));
    }
  };

  const getSelectedItemsWithoutSupplier = () => {
    return filteredItems.filter(item => 
      selectedItems.has(item.id) && !item.primary_supplier_id
    );
  };

  const handleBulkAssignSupplier = async () => {
    if (!bulkSupplierId) {
      toast.error('Please select a supplier');
      return;
    }
    
    const itemsToUpdate = Array.from(selectedItems);
    if (itemsToUpdate.length === 0) {
      toast.error('No items selected');
      return;
    }

    setAssigningBulk(true);
    try {
      await axios.post(`${API_URL}/admin/inventory/bulk-assign-supplier`, {
        item_ids: itemsToUpdate,
        supplier_id: bulkSupplierId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const supplierName = suppliers.find(s => s.id === bulkSupplierId)?.name || 'supplier';
      toast.success(`Assigned ${supplierName} to ${itemsToUpdate.length} item(s)`);
      setShowBulkAssignModal(false);
      setBulkSupplierId('');
      setSelectedItems(new Set());
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign supplier');
    } finally {
      setAssigningBulk(false);
    }
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData(emptyItem);
    setShowItemModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.sku?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || item.category === categoryFilter;
    const matchesLowStock = !showLowStock || (item.quantity <= item.min_stock_level);
    return matchesSearch && matchesCategory && matchesLowStock;
  });

  const isLowStock = (item) => item.quantity <= item.min_stock_level;

  return (
    <div data-testid="inventory-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Inventory</h2>
          <p className="text-neutral-500 text-sm">Manage stock levels for fabrics and hardware</p>
        </div>
        <PermissionGate module="inventory" level="full">
        <Button onClick={openCreateModal} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> Add Item
        </Button>
        </PermissionGate>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{stats.total_items}</p>
                <p className="text-sm text-neutral-500">Total Items</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-amber-600">{stats.low_stock_count}</p>
                <p className="text-sm text-neutral-500">Low Stock</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{formatCurrency(stats.total_value)}</p>
                <p className="text-sm text-neutral-500">Total Value</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_category || {}).map(([cat, data]) => (
                <div key={cat} className="text-xs">
                  <span className="font-medium capitalize">{cat}:</span>
                  <span className="ml-1 text-neutral-500">{data.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Search by name or SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); fetchItems(); }}
          className="px-3 py-2 border border-neutral-200 rounded-lg text-sm"
        >
          <option value="">All Categories</option>
          {CATEGORIES.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
          ))}
        </select>
        <button
          onClick={() => { setShowLowStock(!showLowStock); fetchItems(); }}
          className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
            showLowStock 
              ? 'bg-amber-100 border-amber-300 text-amber-700' 
              : 'bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300'
          }`}
        >
          <AlertTriangle className="w-4 h-4 inline mr-2" />
          Low Stock Only
        </button>
      </div>

      {/* Bulk Action Bar */}
      {selectedItems.size > 0 && (
        <div className="flex items-center justify-between px-4 py-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-3">
            <Checkbox 
              checked={selectedItems.size === filteredItems.length}
              onCheckedChange={toggleSelectAll}
            />
            <span className="text-sm font-medium text-amber-800">
              {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
            </span>
            <button 
              onClick={() => setSelectedItems(new Set())}
              className="text-xs text-amber-600 hover:text-amber-800 underline"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <PermissionGate module="inventory" level="edit">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowBulkAssignModal(true)}
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Assign Supplier
              </Button>
            </PermissionGate>
          </div>
        </div>
      )}

      {/* Items Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-12 bg-neutral-50 rounded-lg">
          <Package className="w-12 h-12 mx-auto text-neutral-400 mb-4" />
          <p className="text-neutral-600">No inventory items found</p>
        </div>
      ) : (
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b">
              <tr>
                <th className="w-10 px-4 py-3">
                  <Checkbox 
                    checked={filteredItems.length > 0 && selectedItems.size === filteredItems.length}
                    onCheckedChange={toggleSelectAll}
                  />
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Item</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">SKU</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Category</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Supplier</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-neutral-600">Qty</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-neutral-600">Min</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-neutral-600">Unit Cost</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-neutral-600">Value</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(item => {
                const itemSupplier = suppliers.find(s => s.id === item.primary_supplier_id);
                return (
                <tr key={item.id} className={`border-b last:border-0 hover:bg-neutral-50 ${isLowStock(item) ? 'bg-amber-50' : ''}`}>
                  <td className="w-10 px-4 py-3">
                    <Checkbox 
                      checked={selectedItems.has(item.id)}
                      onCheckedChange={() => toggleSelectItem(item.id)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-sm">{item.name}</p>
                      {item.color && <p className="text-xs text-neutral-400">{item.color}</p>}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-sm text-neutral-600">{item.sku}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-1 bg-neutral-100 rounded capitalize">{item.category}</span>
                  </td>
                  <td className="px-4 py-3">
                    {itemSupplier ? (
                      <span className="text-sm text-neutral-700">{itemSupplier.name}</span>
                    ) : (
                      <button
                        onClick={() => openEditModal(item)}
                        className="text-xs text-amber-600 hover:text-amber-700 hover:underline"
                        title="Click to assign supplier"
                      >
                        + Assign
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-medium ${isLowStock(item) ? 'text-amber-600' : ''}`}>
                      {item.quantity} {item.unit}
                    </span>
                    {isLowStock(item) && <AlertTriangle className="w-4 h-4 text-amber-500 inline ml-2" />}
                  </td>
                  <td className="px-4 py-3 text-right text-neutral-500">{item.min_stock_level}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(item.unit_cost)}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatCurrency(item.quantity * item.unit_cost)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => { setMovementItem(item); setShowMovementModal(true); }}
                        className="p-1.5 hover:bg-neutral-100 rounded"
                        title="Record movement"
                      >
                        <TrendingDown className="w-4 h-4 text-neutral-500" />
                      </button>
                      <button
                        onClick={() => viewHistory(item)}
                        className="p-1.5 hover:bg-neutral-100 rounded"
                        title="View history"
                      >
                        <History className="w-4 h-4 text-neutral-500" />
                      </button>
                      <PermissionGate module="inventory" level="edit">
                      <button
                        onClick={() => openEditModal(item)}
                        className="p-1.5 hover:bg-neutral-100 rounded"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4 text-neutral-500" />
                      </button>
                      </PermissionGate>
                      <PermissionGate module="inventory" level="full">
                      <button
                        onClick={() => deleteItem(item.id)}
                        className="p-1.5 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                      </PermissionGate>
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Item Modal */}
      <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Item' : 'Add Inventory Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Item name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>SKU</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  placeholder="Auto-generated if empty"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Category</Label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Subcategory</Label>
                <Input
                  value={formData.subcategory}
                  onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  placeholder="e.g., Roller, Roman"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Unit</Label>
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                >
                  {UNITS.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Current Quantity</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Min Stock Level</Label>
                <Input
                  type="number"
                  value={formData.min_stock_level}
                  onChange={(e) => setFormData({ ...formData, min_stock_level: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Unit Cost ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.unit_cost}
                  onChange={(e) => setFormData({ ...formData, unit_cost: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Primary Supplier</Label>
                <select
                  value={formData.primary_supplier_id}
                  onChange={(e) => setFormData({ ...formData, primary_supplier_id: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                >
                  <option value="">Select supplier...</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Lead Time (days)</Label>
                <Input
                  type="number"
                  value={formData.lead_time_days}
                  onChange={(e) => setFormData({ ...formData, lead_time_days: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {formData.category === 'fabric' && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Color</Label>
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Material</Label>
                  <Input
                    value={formData.material}
                    onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Width</Label>
                  <Input
                    value={formData.width}
                    onChange={(e) => setFormData({ ...formData, width: e.target.value })}
                    placeholder="e.g., 54 inches"
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowItemModal(false)}>Cancel</Button>
            <Button onClick={saveItem} className="btn-primary">
              {editingItem ? 'Update' : 'Add'} Item
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Movement Modal */}
      <Dialog open={showMovementModal} onOpenChange={setShowMovementModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Stock Movement</DialogTitle>
          </DialogHeader>
          {movementItem && (
            <div className="space-y-4 py-4">
              <div className="bg-neutral-50 rounded-lg p-3">
                <p className="font-medium">{movementItem.name}</p>
                <p className="text-sm text-neutral-500">Current: {movementItem.quantity} {movementItem.unit}</p>
              </div>

              <div>
                <Label>Movement Type</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {MOVEMENT_TYPES.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setMovementData({ ...movementData, movement_type: type.id })}
                      className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                        movementData.movement_type === type.id
                          ? 'bg-amber-100 border-amber-300 text-amber-700'
                          : 'bg-white border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      {type.name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={movementData.quantity}
                  onChange={(e) => setMovementData({ ...movementData, quantity: parseFloat(e.target.value) || 0 })}
                  placeholder={`Enter quantity in ${movementItem.unit}`}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={movementData.notes}
                  onChange={(e) => setMovementData({ ...movementData, notes: e.target.value })}
                  placeholder="Optional notes..."
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowMovementModal(false)}>Cancel</Button>
            <Button onClick={recordMovement} className="btn-primary">Record Movement</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Supplier Modal */}
      <Dialog open={showBulkAssignModal} onOpenChange={setShowBulkAssignModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Supplier to Selected Items</DialogTitle>
            <DialogDescription>
              Assign a primary supplier to {selectedItems.size} selected inventory item{selectedItems.size !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>{selectedItems.size}</strong> item{selectedItems.size !== 1 ? 's' : ''} will be updated
              </p>
              {getSelectedItemsWithoutSupplier().length > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  {getSelectedItemsWithoutSupplier().length} item(s) currently have no supplier assigned
                </p>
              )}
            </div>
            
            <div>
              <Label>Select Supplier</Label>
              <Select value={bulkSupplierId} onValueChange={setBulkSupplierId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a supplier..." />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkAssignModal(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkAssignSupplier} 
              disabled={!bulkSupplierId || assigningBulk}
              className="btn-primary"
            >
              {assigningBulk ? 'Assigning...' : 'Assign Supplier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Movement History</DialogTitle>
          </DialogHeader>
          {historyItem && (
            <div className="py-4">
              <div className="bg-neutral-50 rounded-lg p-3 mb-4">
                <p className="font-medium">{historyItem.name}</p>
                <p className="text-sm text-neutral-500">SKU: {historyItem.sku}</p>
              </div>

              {movements.length === 0 ? (
                <p className="text-center text-neutral-500 py-8">No movement history</p>
              ) : (
                <div className="space-y-2">
                  {movements.map(m => (
                    <div key={m.id} className="flex items-center justify-between bg-white border border-neutral-200 rounded-lg p-3">
                      <div>
                        <p className="text-sm font-medium capitalize">{m.movement_type}</p>
                        <p className="text-xs text-neutral-500">
                          {new Date(m.created_at).toLocaleDateString()} by {m.performed_by}
                        </p>
                        {m.notes && <p className="text-xs text-neutral-400 mt-1">{m.notes}</p>}
                      </div>
                      <div className="text-right">
                        <p className={`font-medium ${m.quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {m.quantity > 0 ? '+' : ''}{m.quantity} {m.unit}
                        </p>
                        <p className="text-xs text-neutral-400">{m.quantity_before} → {m.quantity_after}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryTab;
